import { produce } from 'immer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  DefaultDescriptionBenefitEducation,
  FormBase,
  FormModal,
  type IBaseModalProps,
  formatUnknownErrorMessage,
  useToast,
} from '@gbs-monorepo-packages/common';
import {
  type RestorePageSchema,
  restorePageSchema,
} from '@gbs-monorepo/edge/formSchemas/pageSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import { CenteredText } from '../../../../components/CenteredText';
import type {
  IApiThrowsError,
  IPaginationMetaProps,
} from '../../../../services/api';
import {
  type ICoursePageDTO,
  type ICoursePageHistory,
  type IHistoryPage,
  getCoursePageHistory,
} from '../../../../services/coursePages';
import Logger from '../../../../utils/logger';
import {
  CheckIcon,
  CheckIconContainer,
  HistoryIcon,
  HistoryIconContainer,
  HistoryVersionDate,
  HistoryVersionDateContainer,
  HistoryVersionItem,
  HistoryVersionItemsContainer,
  Loading,
  LoadingContainer,
  NoHistoryText,
} from './styles';

interface IRestorePageModalProps
  extends Partial<Omit<IBaseModalProps, 'children' | 'onOpenChange'>> {
  onAccept: (pageHistoryVersion: IHistoryPage) => void;
  onDecline: () => void;
  open: boolean;
  coursePage?: ICoursePageDTO;
  waitToOpen?: boolean;
}

export const RestorePageModal = ({
  onAccept,
  onDecline,
  open,
  coursePage,
  waitToOpen = false,
  ...props
}: IRestorePageModalProps): JSX.Element | null => {
  const { addToast } = useToast();
  const [loadingPageHistory, setLoadingPageHistory] = useState(true);
  const loadedHistoryOnce = useRef(false);
  const [paginationMeta, setPaginationMeta] =
    useState<IPaginationMetaProps | null>(null);

  const [pageHistory, setPageHistory] = useState<IHistoryPage[]>([]);

  const restorePageForm = useForm<RestorePageSchema>({
    resolver: zodResolver(restorePageSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      version: undefined,
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
    setValue,
    watch,
    reset,
  } = restorePageForm;

  const values = watch();

  useEffect(() => {
    if (open) {
      reset({
        version: undefined,
      });
    }
  }, [open, reset]);

  const onSubmit: SubmitHandler<RestorePageSchema> = useCallback(
    (data: RestorePageSchema) => {
      const selectedHistoryVersion = pageHistory.find(
        (historyPage) => historyPage.version === data.version
      );

      if (selectedHistoryVersion) {
        onAccept?.(selectedHistoryVersion);
        reset({
          version: undefined,
        });
        loadedHistoryOnce.current = false;
      }
    },
    [onAccept, pageHistory, reset]
  );

  const handleDeclineRestorePage = useCallback(() => {
    onDecline?.();
    reset({
      version: undefined,
    });
    loadedHistoryOnce.current = false;
    setPageHistory([]);
  }, [onDecline, reset]);

  useEffect(() => {
    let mount = true;

    const loadPageHistoryOnce = async () => {
      if (!coursePage) {
        addToast({
          title: 'Error loading page history',
          description: DefaultDescriptionBenefitEducation,
          styleType: 'error',
          dataCy: 'error-toast',
          duration: 3000,
        });
        handleDeclineRestorePage();
        return;
      }

      loadedHistoryOnce.current = true;
      setLoadingPageHistory(true);
      try {
        if (coursePage.id > 0) {
          const history: ICoursePageHistory = await getCoursePageHistory({
            coursePageId: coursePage.id,
            page: 1,
            limit: 15,
          });
          if (mount) {
            setPageHistory(history.data);
            setPaginationMeta(history.meta);
          }
        } else {
          if (mount) {
            setPageHistory([]);
            setPaginationMeta(null);
          }
        }
      } catch (err) {
        loadedHistoryOnce.current = false;

        addToast({
          title: 'Error loading page history',
          description: formatUnknownErrorMessage(
            err,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'error-toast',
          duration: 3000,
        });
      } finally {
        setLoadingPageHistory(false);
      }
    };

    if (open && !loadedHistoryOnce.current) {
      void loadPageHistoryOnce();
    }

    return () => {
      mount = false;
    };
  }, [addToast, coursePage, handleDeclineRestorePage, open]);

  const searchCoursePageHistory = useCallback(
    (searchByButton = false) => {
      if (!loadingPageHistory || searchByButton) {
        const pageAux = searchByButton
          ? 0
          : Number((paginationMeta?.page ?? 0) > 0 ? paginationMeta?.page : 0);
        getCoursePageHistory({
          coursePageId: coursePage?.id ?? 0,
          page: pageAux + 1,
          limit: 15,
        })
          .then((response: ICoursePageHistory) => {
            if (response.data !== null) {
              setPageHistory(
                produce((draft) => {
                  draft.push(...response.data);
                })
              );
            }
            setPaginationMeta(response.meta);
          })
          .catch((error: IApiThrowsError) => {
            Logger.debug('error: ', error);
          });
      }
    },
    [coursePage?.id, loadingPageHistory, paginationMeta?.page]
  );

  return !open ? null : (
    <FormBase.Provider {...restorePageForm}>
      <FormModal
        acceptText="Restore"
        dataCy="restore-course-page-dialogModal"
        declineText="Cancel"
        mainText={`${coursePage?.title ?? 'Page'} History`}
        {...props}
        onAccept={handleSubmit(onSubmit)}
        onDecline={handleDeclineRestorePage}
        onOpenChange={handleDeclineRestorePage}
        open={!waitToOpen}
        disabled={!isValid}
      >
        {loadingPageHistory ? (
          <LoadingContainer data-cy="loading-page-history">
            <Loading />
          </LoadingContainer>
        ) : (
          <HistoryVersionItemsContainer>
            {pageHistory.length > 0 ? (
              <InfiniteScroll
                height={400}
                style={{
                  overflow: 'auto',
                  maxHeight: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                }}
                dataLength={pageHistory?.length}
                next={searchCoursePageHistory}
                hasMore={pageHistory.length < (paginationMeta?.total ?? 0)}
                loader={
                  <LoadingContainer data-cy="loading-teammates-container">
                    <Loading />
                  </LoadingContainer>
                }
                endMessage={
                  <CenteredText
                    message="You have seen it all"
                    dataCy="pagination-endLimit"
                  />
                }
              >
                {pageHistory?.map((historyPage: IHistoryPage) => (
                  <HistoryVersionItem
                    key={historyPage.version}
                    onClick={() => {
                      setValue('version', historyPage.version, {
                        shouldValidate: true,
                      });
                    }}
                  >
                    <HistoryIconContainer>
                      <HistoryIcon />
                    </HistoryIconContainer>
                    <HistoryVersionDateContainer>
                      <HistoryVersionDate>
                        Version from{' '}
                        {new Date(historyPage.loggedAt).toLocaleString()}
                      </HistoryVersionDate>
                    </HistoryVersionDateContainer>
                    <CheckIconContainer>
                      {values.version === historyPage.version ? (
                        <CheckIcon />
                      ) : null}
                    </CheckIconContainer>
                  </HistoryVersionItem>
                ))}
              </InfiniteScroll>
            ) : (
              <NoHistoryText>No page history available.</NoHistoryText>
            )}
          </HistoryVersionItemsContainer>
        )}
      </FormModal>
    </FormBase.Provider>
  );
};
